import { Injectable, OnDestroy } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from './auth.service';
import { BehaviorSubject, of, throttleTime } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  socketError() {
    throw new Error('Method not implemented.');
  }
  private serverTime$ = new BehaviorSubject<any>({});
  private bidRefresh$ = new BehaviorSubject<any>({});
  private ABPRefresh$ = new BehaviorSubject<any>({});

  private errorResponse$ = new BehaviorSubject<any>({});
  private sheetRefresh$ = new BehaviorSubject<any>({});
  private getDivision$ = new BehaviorSubject<any>({});
  private bulkABPSuccess$ = new BehaviorSubject<any>({});

  private worker: Worker | null = null;
  private isConnected = false; // Track connection status
  private emitQueue: Array<{ event: string; payload: any }> = [];
  private reconnecting = false;
  private safeEmit(event: string, payload: any) {
    if (this.socket.ioSocket?.connected) {
      this.socket.emit(event, payload);
      return;
    }

    // Queue emit during idle / reconnect
    this.emitQueue.push({ event, payload });

    if (!this.reconnecting) {
      this.reconnecting = true;

      this.socket.connect();
      console.log('Reconnected BLOG');
      this.socket.once('connect', () => {
        this.reconnecting = false;
        console.log('Reconnected');
        while (this.emitQueue.length) {
          const item = this.emitQueue.shift();
          this.socket.emit(item!.event, item!.payload);
        }
      });
    }
  }

  constructor(
    private socket: Socket,
    private toastr: ToastrService,
    private authService: AuthService,
  ) {}
  connectSocket() {
    if (this.isConnected) return; // Prevent duplicate connections

    if (typeof Worker !== 'undefined') {
      if (!this.worker) {
        this.worker = new Worker(`/assets/socket.worker.js?v=${Date.now()}`);
      }

      this.worker.postMessage({
        type: 'connect',
        serverUrl: environment.SOCKET_URL,
      });
      this.isConnected = true; // Mark as connected

      this.worker.onmessage = ({ data }) => {
        const { event, data: payload } = data;
        switch (event) {
          case 'serverTime':
            this.serverTime$.next(payload);
            break;
          case 'bidRefresh':
            this.bidRefresh$.next(payload);
            break;
          case 'sheetRefresh':
            this.sheetRefresh$.next(payload);
            break;
          case 'ABPRefresh':
            this.ABPRefresh$.next(payload);
            break;
          case 'bulkABPSuccess':
            this.bulkABPSuccess$.next(payload);
            break;
          case 'getDivision':
            this.getDivision$.next(payload);
            break;
          case 'error_notify':
            this.errorResponse$.next(payload);
            break;
        }
      };
    } else {
      console.warn('Web Workers are not supported in this browser.');
    }
  }
  getServerTime() {
    return this.serverTime$.asObservable();
  }
  getBidRefresh() {
    return this.bidRefresh$.asObservable();
  }
  getABPRefresh() {
    return this.ABPRefresh$.asObservable();
  }
  getBulkABPSuccess() {
    return this.bulkABPSuccess$.asObservable();
  }

  getSheetRefresh() {
    return this.sheetRefresh$.asObservable();
  }
  getDivision() {
    return this.getDivision$.asObservable();
  }
  getErrorResponse() {
    return this.errorResponse$.asObservable();
  }

  /**
   * Fetch Invoices
   * @fetchInvoices Emit Event
   * @onFetchInvoices Listen Event
   */

  fetchInvoices(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    this.safeEmit('fetchInvoices', payload);
  }
  redirectActiveSeller(payload: any) {
    // console.log('payload: ', payload)

    this.safeEmit('sheetRefreshSeller', payload);
  }

  onFetchInvoices() {
    return this.socket.fromOneTimeEvent('fetchInvoices');
  }

  /**
   * Submit Invoice Comment
   * @submitComment Emit Event
   */
  submitComment(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('placeComment', payload);
  }

  /**
   * Place Bid
   * @placeBid Emit Event
   */
  placeBid(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    //this.socket.emit('bidSubmit', payload);

    if (this.worker) {
      this.worker.postMessage({
        type: 'bidSubmit',
        payload: payload,
        serverUrl: environment.SOCKET_URL,
      });
    }
  }
  updateBid(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    //this.socket.emit('bidSubmit', payload);
    if (this.worker) {
      this.worker.postMessage({
        type: 'bidUpdate',
        payload: payload,
        serverUrl: environment.SOCKET_URL,
      });
    }
  }

  /**
   * Fetch Invoice User Log History
   * @fetchInvoices Emit Event
   * @onFetchInvoices Listen Event
   */

  fetchInvoiceBidLogs(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('bidLog', payload);
  }

  onFetchInvoiceBidLogs() {
    return this.socket.fromOneTimeEvent('bidLog');
  }

  /**
   * Update Price Idea
   * @updatePriceIdea Emit Event
   */
  updatePriceIdea(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('priceUpdate', payload);
  }

  /**
   * Invoice Bid Lock
   * @invoiceBidLock Emit Event
   */
  invoiceBidLock(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    this.safeEmit('bidLock', payload);
  }

  exposeBuyer(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('exposeBuyer', payload);
  }
  extendedSale(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('extendedSale', payload);
  }
  onExtendedSaleSeller() {
    return this.socket.fromOneTimeEvent('extendedSale');
  }
  gardenermail(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    this.safeEmit('gardenermail', payload);
  }

  bulkABP(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    //console.log('payload '+ JSON.stringify(payload));
    this.safeEmit('bulkABP', payload);
  }

  /**
   * Buyer Place ABP
   * @buyerPlaceAbp Emit Event
   */
  buyerPlaceAbp(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    this.safeEmit('placeABP', payload);
  }
  buyerPlaceBulkAbp(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    this.safeEmit('bulkABP', payload);
  }

  onBuyerAbpPlaced() {
    return this.socket.fromOneTimeEvent('placeABP');
  }

  /**
   * Seller Place Auto Price
   * @sellerPlaceAutoPrice Emit Event
   */
  sellerPlaceAutoPrice(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    this.safeEmit('autoprice', payload);
  }

  onSellerPlaceAutoPrice() {
    return this.socket.fromOneTimeEvent('autoprice');
  }

  /**
   * Send Division Lot Request
   * @sendDivisionLotRequest Emit Event
   */
  sendDivisionLotRequest(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('sendDivision', payload);
  }

  /**
   * Action for Division Lot Request
   * @divisionLotRequestAction Emit Event
   */
  divisionLotRequestAction(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('acceptORrejectDivision', payload);
  }

  /**
   * Seller Generate Report
   * @sellerReportGenerate Emit Event
   * @onSellerReportGenerate Listen Event
   */

  sellerReportGenerate(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    this.safeEmit('sellerReport', payload);
  }

  onSellerReportGenerate() {
    return this.socket.fromOneTimeEvent('sellerReport');
  }

  /**
   * Seller Generate Report
   * @buyerReportGenerate Emit Event
   * @onbuyerReportGenerate Listen Event
   */

  buyerReportGenerate(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    this.safeEmit('buyerReport', payload);
  }

  onBuyerReportGenerate() {
    return this.socket.fromOneTimeEvent('buyerReport');
  }

  /**
   * Export Sheet - Excel
   * @sheetExportExcel Emit Event
   * @onSheetExportExcel Listen Event
   */

  sheetExportExcel(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    this.safeEmit('downloadExcel', payload);
  }

  onSheetExportExcel() {
    return this.socket.fromOneTimeEvent('downloadExcel');
  }
  /**
   * Export Sheet - Excel
   * @buyerPurchaseReport Emit Event
   * @onBuyerPurchaseReport Listen Event
   */
  buyerPurchaseReport(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    this.safeEmit('buyerPurchaseReport', payload);
  }
  onBuyerPurchaseReport() {
    return this.socket.fromEvent('buyerPurchaseReport');
  }
  /**
   * Export Sheet - Excel
   * @buyerPurchaseReport Emit Event
   * @onBuyerPurchaseReport Listen Event
   */
  downloadClosedExcel(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('downloadClosedExcel', payload);
  }

  ondownloadClosedExcel() {
    return this.socket.fromEvent('downloadClosedExcel');
  }
  /**
   * Export Sheet - Excel
   * @sellerDownloadClosedExcel Emit Event
   * @onsellerDownloadClosedExcel Listen Event
   */
  sellerDownloadClosedExcel(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();
    //console.log(payload);return;
    this.safeEmit('downloadSellerClosedExcel', payload);
  }

  onsellerDownloadClosedExcel() {
    return this.socket.fromEvent('downloadSellerClosedExcel');
  }

  /**
   * Export Sheet - PDF (Seller)
   * @sellerSheetExportPdf Emit Event
   * @onSellerSheetExportPdf Listen Event
   */

  sellerSheetExportPdf(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('sheetDetailsPDFprintForSeller', payload);
  }

  onSellerSheetExportPdf() {
    return this.socket.fromOneTimeEvent('sheetDetailsPDFprintForSeller');
  }

  /**
   * Export Sheet - PDF (Buyer)
   * @buyerSheetExportPdf Emit Event
   * @onBuyerSheetExportPdf Listen Event
   */

  buyerSheetExportPdf(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('sheetDetailsPDFprintForBuyer', payload);
  }

  onBuyerSheetExportPdf() {
    return this.socket.fromOneTimeEvent('sheetDetailsPDFprintForBuyer');
  }

  /**
   * Seller Close Sheet Request
   * @sellerCloseSheetRequest Emit Event
   * @onSellerCloseSheetRequest Listen Event
   */

  sellerCloseSheetRequest(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('sellerCloseSheetRequest', payload);
  }

  onSellerCloseSheetRequest() {
    return this.socket.fromOneTimeEvent('sellerCloseSheetRequest');
  }

  /**
   * Seller Close Sheet Request
   * @buyerCloseSheetRequestAccept Emit Event
   * @onBuyerCloseSheetRequestAccept Listen Event
   */

  buyerCloseSheetRequestAccept(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('buyerCloseSheetRequestAccept', payload);
  }

  onBuyerCloseSheetRequestAccept() {
    return this.socket.fromOneTimeEvent('buyerCloseSheetRequestAccept');
  }

  /**
   * Buyer Get Buying Figures
   * @buyerGetBuyingFigures Emit Event
   * @onBuyerGetBuyingFigures Listen Event
   */

  buyerGetBuyingFigures(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('buyingFigure', payload);
  }

  onBuyerGetBuyingFigures() {
    return this.socket.fromOneTimeEvent('buyingFigure');
  }

  /**
   * Seller Get Buying Figures
   * @sellerGetBuyingFigures Emit Event
   * @onSellerGetBuyingFigures Listen Event
   */

  sellerGetBuyingFigures(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('buyingFigureSeller', payload);
  }

  onSellerGetBuyingFigures() {
    return this.socket.fromOneTimeEvent('buyingFigureSeller');
  }

  /**
   * Buyer Get Buying Figures
   * @buyerFetchExpiringLots Emit Event
   * @onBuyerFetchExpiringLots Listen Event
   */

  buyerFetchExpiringLots(payload: any) {
    payload.AUTHENTICATION_KEY = this.authService.getToken();

    this.safeEmit('expiryLotsInformation', payload);
  }

  onBuyerFetchExpiringLots() {
    return this.socket.fromOneTimeEvent('expiryLotsInformation');
  }
  disconnectSocket() {
    if (this.worker) {
      this.worker.postMessage({ type: 'disconnect' }); // Tell worker to disconnect socket
      //this.worker.terminate();
      this.worker = null;
      this.isConnected = false; // Mark as bidSub
    }
  }
}
